import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { validateAppointmentSchedule } from '../../../utils/appointmentConfig';
import { useAppointmentAlert } from '../../../contexts/AppointmentAlertContext';

// ─── AgendarTrabajoModal ──────────────────────────────────────────────────────
// Props:
//   isOpen      — boolean
//   onClose     — () => void
//   contact     — crm_contact object (required)
//   activity    — crm_activity object (optional)
//   onSuccess   — () => void

const AgendarTrabajoModal = ({ isOpen, onClose, contact, activity, onSuccess }) => {
  const { userProfile } = useAuth();
  const { showAppointmentAlert } = useAppointmentAlert();

  const today = new Date()?.toISOString()?.split('T')?.[0];

  const [form, setForm] = useState({
    clienteNombre: '',
    unidadMarcaModelo: '',
    unidadPlacasVin: '',
    servicioTrabajo: '',
    fecha: today,
    hora: '',
    mecanicoId: '',
    notas: '',
    reminderMinutes: null,
  });

  const [mecanicos, setMecanicos] = useState([]);
  const [loadingMecanicos, setLoadingMecanicos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill form when contact/activity changes
  useEffect(() => {
    if (!isOpen) return;
    setForm({
      clienteNombre: contact?.nombre || '',
      unidadMarcaModelo: '',
      unidadPlacasVin: '',
      servicioTrabajo: activity?.notas || contact?.servicio_interes || '',
      fecha: today,
      hora: activity?.hora?.slice(0, 5) || '',
      mecanicoId: '',
      notas: '',
      reminderMinutes: activity?.reminder_minutes ?? null,
    });
    setError('');
  }, [isOpen, contact, activity]);

  // Load mechanics
  useEffect(() => {
    if (!isOpen) return;
    const loadMecanicos = async () => {
      setLoadingMecanicos(true);
      try {
        const { data } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, email, role')
          ?.eq('role', 'taller')
          ?.eq('is_active', true)
          ?.order('full_name');
        setMecanicos(data || []);
      } catch {
        setMecanicos([]);
      } finally {
        setLoadingMecanicos(false);
      }
    };
    loadMecanicos();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const generateFolioOT = () =>
    'OT-' +
    Date.now()?.toString(36)?.toUpperCase() +
    '-' +
    Math.random()?.toString(36)?.substr(2, 3)?.toUpperCase();

  const buildUnidadInfo = () => {
    const parts = [form?.unidadMarcaModelo, form?.unidadPlacasVin]?.filter(Boolean);
    return parts?.join(' | ') || '—';
  };

  const validate = () => {
    if (!form?.clienteNombre?.trim()) return 'El nombre del cliente es requerido';
    if (!form?.servicioTrabajo?.trim()) return 'El servicio / trabajo es requerido';
    if (!form?.fecha) return 'La fecha es requerida';
    return '';
  };

  // ── Agendar trabajo ──────────────────────────────────────────────────────────
  const handleAgendarTrabajo = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    if (!form?.hora) { setError('La hora es requerida para agendar'); return; }

    // ── Validación de horario laboral ──────────────────────────────────────────
    const scheduleCheck = validateAppointmentSchedule(form?.fecha, form?.hora);
    if (!scheduleCheck?.valid) { setError(scheduleCheck?.error); return; }

    setSaving(true);
    setError('');
    try {
      const folioOt = generateFolioOT();
      const now = new Date()?.toISOString();
      const unidadInfo = buildUnidadInfo();

      // 1. Insert work order
      const { data: otData, error: otError } = await supabase
        ?.from('work_orders')
        ?.insert({
          folio_ot: folioOt,
          work_status: 'created',
          scope_of_work: [{ descripcion: form?.servicioTrabajo }],
          assigned_to_user_id: form?.mecanicoId || null,
          assigned_at: form?.mecanicoId ? now : null,
          crm_origin: 'crm',
          crm_contact_id: contact?.id || null,
          crm_activity_id: activity?.id || null,
          notas: form?.notas || null,
          company_id: userProfile?.company_id,
          created_by: userProfile?.id,
        })
        ?.select('id')
        ?.single();

      if (otError) throw otError;

      // 2. Insert calendar event (with reminder_minutes)
      const { error: calError } = await supabase
        ?.from('calendar_events')
        ?.insert({
          title: 'Servicio: ' + form?.servicioTrabajo,
          description: 'Cliente: ' + form?.clienteNombre + ' | ' + unidadInfo,
          related_type: 'work_order',
          related_id: otData?.id,
          start_datetime: form?.fecha + 'T' + form?.hora,
          assigned_to: form?.mecanicoId || null,
          status: 'scheduled',
          company_id: userProfile?.company_id,
          created_by: userProfile?.id,
          reminder_minutes: form?.reminderMinutes !== null && form?.reminderMinutes !== undefined
            ? parseInt(form?.reminderMinutes, 10)
            : null,
        });

      if (calError) throw calError;

      // 3. Update activity status if present
      if (activity?.id) {
        await supabase
          ?.from('crm_activities')
          ?.update({ estado: 'realizado' })
          ?.eq('id', activity?.id);
      }

      // ── Alerta visual de cita ──────────────────────────────────────────────
      showAppointmentAlert({
        tipo: 'nueva',
        origen: 'CRM',
        cliente: form?.clienteNombre,
        telefono: contact?.telefono || contact?.phone || null,
        vehiculo: form?.unidadMarcaModelo || null,
        placa: form?.unidadPlacasVin || null,
        servicio: form?.servicioTrabajo,
        fecha: form?.fecha,
        hora: form?.hora,
        notas: form?.notas || null,
        usuario: userProfile?.full_name || userProfile?.email || 'Sistema',
      });

      showToastExternal('Trabajo agendado correctamente');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al agendar el trabajo');
    } finally {
      setSaving(false);
    }
  };

  // ── Realizar ahora ───────────────────────────────────────────────────────────
  const handleRealizarAhora = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSaving(true);
    setError('');
    try {
      const folioOt = generateFolioOT();
      const now = new Date()?.toISOString();

      // 1. Insert work order in progress
      const { error: otError } = await supabase
        ?.from('work_orders')
        ?.insert({
          folio_ot: folioOt,
          work_status: 'in_progress',
          scope_of_work: [{ descripcion: form?.servicioTrabajo }],
          assigned_to_user_id: form?.mecanicoId || null,
          assigned_at: form?.mecanicoId ? now : null,
          started_at: now,
          crm_origin: 'crm',
          crm_contact_id: contact?.id || null,
          crm_activity_id: activity?.id || null,
          notas: form?.notas || null,
          company_id: userProfile?.company_id,
          created_by: userProfile?.id,
        });

      if (otError) throw otError;

      // 2. Update activity status if present
      if (activity?.id) {
        await supabase
          ?.from('crm_activities')
          ?.update({ estado: 'realizado' })
          ?.eq('id', activity?.id);
      }

      showToastExternal('OT generada y enviada a Taller');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Error al generar la OT');
    } finally {
      setSaving(false);
    }
  };

  // Internal toast helper — parent handles toast via onSuccess, but we show inline error
  // For success toasts we use a local state toast
  const [localToast, setLocalToast] = useState(null);
  const showToastExternal = (msg) => {
    setLocalToast(msg);
    setTimeout(() => setLocalToast(null), 3000);
  };

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary';
  const labelClass = 'block text-sm font-medium text-foreground mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="CalendarPlus" size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">Agendar / Realizar trabajo</h2>
              {contact?.nombre && (
                <p className="text-xs text-muted-foreground">{contact?.nombre}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {/* Cliente */}
          <div>
            <label className={labelClass}>
              Cliente <span className="text-red-500">*</span>
            </label>
            <input
              value={form?.clienteNombre}
              onChange={(e) => handleChange('clienteNombre', e?.target?.value)}
              className={inputClass}
              placeholder="Nombre del cliente"
            />
          </div>

          {/* Unidad */}
          <div>
            <label className={labelClass}>Unidad</label>
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form?.unidadMarcaModelo}
                onChange={(e) => handleChange('unidadMarcaModelo', e?.target?.value)}
                className={inputClass}
                placeholder="Marca / Modelo / Año"
              />
              <input
                value={form?.unidadPlacasVin}
                onChange={(e) => handleChange('unidadPlacasVin', e?.target?.value)}
                className={inputClass}
                placeholder="Placas / VIN"
              />
            </div>
          </div>

          {/* Servicio / trabajo */}
          <div>
            <label className={labelClass}>
              Servicio / trabajo <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form?.servicioTrabajo}
              onChange={(e) => handleChange('servicioTrabajo', e?.target?.value)}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Describe el servicio o trabajo a realizar..."
            />
          </div>

          {/* Fecha + Hora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>
                Fecha <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form?.fecha}
                onChange={(e) => handleChange('fecha', e?.target?.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Hora</label>
              <input
                type="time"
                value={form?.hora}
                onChange={(e) => handleChange('hora', e?.target?.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Recordatorio */}
          <div>
            <label className={labelClass}>Recordatorio</label>
            <select
              value={form?.reminderMinutes === null || form?.reminderMinutes === undefined ? '' : form?.reminderMinutes}
              onChange={(e) => handleChange('reminderMinutes', e?.target?.value === '' ? null : parseInt(e?.target?.value, 10))}
              className={inputClass}
            >
              <option value="">Sin recordatorio</option>
              <option value="10">10 minutos antes</option>
              <option value="15">15 minutos antes</option>
              <option value="20">20 minutos antes</option>
              <option value="30">30 minutos antes</option>
            </select>
          </div>

          {/* Mecánico asignado */}
          <div>
            <label className={labelClass}>Mecánico asignado</label>
            {loadingMecanicos ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background text-sm text-muted-foreground">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Cargando mecánicos...
              </div>
            ) : (
              <select
                value={form?.mecanicoId}
                onChange={(e) => handleChange('mecanicoId', e?.target?.value)}
                className={inputClass}
              >
                <option value="">Sin asignar</option>
                {mecanicos?.map((m) => (
                  <option key={m?.id} value={m?.id}>
                    {m?.full_name || m?.email}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Notas */}
          <div>
            <label className={labelClass}>Notas</label>
            <textarea
              value={form?.notas}
              onChange={(e) => handleChange('notas', e?.target?.value)}
              rows={2}
              className={`${inputClass} resize-none`}
              placeholder="Notas adicionales..."
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <Icon name="AlertCircle" size={14} />
              {error}
            </div>
          )}

          {/* Success toast inline */}
          {localToast && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
              <Icon name="CheckCircle" size={14} />
              {localToast}
            </div>
          )}

          {/* Info box */}
          <div className="bg-muted/40 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
            <p className="flex items-center gap-1.5 font-medium text-foreground">
              <Icon name="Info" size={12} className="text-primary" />
              ¿Cuál es la diferencia?
            </p>
            <p>
              <span className="font-medium">Agendar trabajo:</span> Crea un evento en el calendario y genera la OT con estatus "Agendado".
            </p>
            <p>
              <span className="font-medium">Realizar ahora:</span> Genera la OT directamente y la envía a Taller con estatus "En proceso".
            </p>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 p-5 pt-0">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 border-primary text-primary hover:bg-primary/10"
            onClick={handleAgendarTrabajo}
            disabled={saving}
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Guardando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Icon name="CalendarPlus" size={15} />
                Agendar trabajo
              </span>
            )}
          </Button>
          <Button
            type="button"
            variant="default"
            className="flex-1"
            onClick={handleRealizarAhora}
            disabled={saving}
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Guardando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Icon name="Wrench" size={15} />
                Realizar ahora
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgendarTrabajoModal;
